import * as React from 'react';
import { cn } from '../../utils/cn';
import { cva } from 'class-variance-authority';

// Heading variants using CVA for consistency
const headingVariants = cva('font-semibold tracking-tight text-foreground', {
    variants: {
        size: {
            h1: 'text-4xl lg:text-5xl',
            h2: 'text-3xl lg:text-4xl',
            h3: 'text-2xl lg:text-3xl',
            h4: 'text-xl lg:text-2xl',
            h5: 'text-lg lg:text-xl',
            h6: 'text-base lg:text-lg',
        },
    },
    defaultVariants: {
        size: 'h1',
    },
});

// Text/Paragraph variants
const textVariants = cva('text-foreground', {
    variants: {
        size: {
            xs: 'text-xs',
            sm: 'text-sm',
            base: 'text-base',
            lg: 'text-lg',
            xl: 'text-xl',
            '2xl': 'text-2xl',
        },
        weight: {
            normal: 'font-normal',
            medium: 'font-medium',
            semibold: 'font-semibold',
            bold: 'font-bold',
        },
        color: {
            default: 'text-foreground',
            muted: 'text-muted-foreground',
            primary: 'text-primary',
            destructive: 'text-destructive',
            success: 'text-green-600 dark:text-green-400',
            warning: 'text-yellow-600 dark:text-yellow-400',
        },
        align: {
            left: 'text-left',
            center: 'text-center',
            right: 'text-right',
            justify: 'text-justify',
        },
    },
    defaultVariants: {
        size: 'base',
        weight: 'normal',
        color: 'default',
        align: 'left',
    },
});

// H1 Component
export const H1 = React.forwardRef(({ className, children, ...props }, ref) => (
    <h1
        ref={ref}
        className={cn(headingVariants({ size: 'h1' }), className)}
        {...props}
    >
        {children}
    </h1>
));
H1.displayName = 'H1';

// H2 Component
export const H2 = React.forwardRef(({ className, children, ...props }, ref) => (
    <h2
        ref={ref}
        className={cn(headingVariants({ size: 'h2' }), className)}
        {...props}
    >
        {children}
    </h2>
));
H2.displayName = 'H2';

// H3 Component
export const H3 = React.forwardRef(({ className, children, ...props }, ref) => (
    <h3
        ref={ref}
        className={cn(headingVariants({ size: 'h3' }), className)}
        {...props}
    >
        {children}
    </h3>
));
H3.displayName = 'H3';

// H4 Component
export const H4 = React.forwardRef(({ className, children, ...props }, ref) => (
    <h4
        ref={ref}
        className={cn(headingVariants({ size: 'h4' }), className)}
        {...props}
    >
        {children}
    </h4>
));
H4.displayName = 'H4';

// H5 Component
export const H5 = React.forwardRef(({ className, children, ...props }, ref) => (
    <h5
        ref={ref}
        className={cn(headingVariants({ size: 'h5' }), className)}
        {...props}
    >
        {children}
    </h5>
));
H5.displayName = 'H5';

// H6 Component
export const H6 = React.forwardRef(({ className, children, ...props }, ref) => (
    <h6
        ref={ref}
        className={cn(headingVariants({ size: 'h6' }), className)}
        {...props}
    >
        {children}
    </h6>
));
H6.displayName = 'H6';

// Text/Paragraph Component with more control
export const Text = React.forwardRef(
    (
        {
            className,
            size = 'base',
            weight = 'normal',
            color = 'default',
            align = 'left',
            as: Component = 'p',
            children,
            ...props
        },
        ref,
    ) => (
        <Component
            ref={ref}
            className={cn(
                textVariants({ size, weight, color, align }),
                className,
            )}
            {...props}
        >
            {children}
        </Component>
    ),
);
Text.displayName = 'Text';

// Specialized text components for common use cases

// Large display text
export const Display = React.forwardRef(
    ({ className, children, ...props }, ref) => (
        <p
            ref={ref}
            className={cn(
                'text-4xl lg:text-5xl font-bold tracking-tight text-foreground',
                className,
            )}
            {...props}
        >
            {children}
        </p>
    ),
);
Display.displayName = 'Display';

// Lead paragraph (for introductions)
export const Lead = React.forwardRef(
    ({ className, children, ...props }, ref) => (
        <p
            ref={ref}
            className={cn('text-xl text-muted-foreground', className)}
            {...props}
        >
            {children}
        </p>
    ),
);
Lead.displayName = 'Lead';

// Small text
export const Small = React.forwardRef(
    ({ className, children, ...props }, ref) => (
        <small
            ref={ref}
            className={cn('text-sm text-muted-foreground', className)}
            {...props}
        >
            {children}
        </small>
    ),
);
Small.displayName = 'Small';

// Muted text (for secondary content)
export const Muted = React.forwardRef(
    ({ className, children, ...props }, ref) => (
        <p
            ref={ref}
            className={cn('text-muted-foreground', className)}
            {...props}
        >
            {children}
        </p>
    ),
);
Muted.displayName = 'Muted';

// Inline code
export const Code = React.forwardRef(
    ({ className, children, ...props }, ref) => (
        <code
            ref={ref}
            className={cn(
                'relative rounded bg-muted px-[0.3rem] py-[0.2rem] font-mono text-sm',
                className,
            )}
            {...props}
        >
            {children}
        </code>
    ),
);
Code.displayName = 'Code';

// Blockquote
export const Blockquote = React.forwardRef(
    ({ className, children, ...props }, ref) => (
        <blockquote
            ref={ref}
            className={cn(
                'border-l-4 border-border pl-4 italic text-muted-foreground',
                className,
            )}
            {...props}
        >
            {children}
        </blockquote>
    ),
);
Blockquote.displayName = 'Blockquote';

// List components
export const List = React.forwardRef(
    ({ className, ordered = false, children, ...props }, ref) => {
        const Component = ordered ? 'ol' : 'ul';
        return (
            <Component
                ref={ref}
                className={cn(
                    'ml-6 space-y-1',
                    ordered ? 'list-decimal' : 'list-disc',
                    className,
                )}
                {...props}
            >
                {children}
            </Component>
        );
    },
);
List.displayName = 'List';

export const ListItem = React.forwardRef(
    ({ className, children, ...props }, ref) => (
        <li ref={ref} className={cn('text-foreground', className)} {...props}>
            {children}
        </li>
    ),
);
ListItem.displayName = 'ListItem';

// Export variants for external use
export { headingVariants, textVariants };
